/* eslint-disable no-nested-ternary */
import { useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import { getIndexingJobDetails, getIndexingJobs, type IndexingJob } from "@/api/admin-api"
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  History,
  Loader2,
  RefreshCw,
} from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Progress } from "@/components/ui/progress"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Skeleton } from "@/components/ui/skeleton"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

type JobStatus = "running" | "completed" | "failed" | "pending" | "cancelled"

const STATUS_CONFIG: Record<
  JobStatus,
  {
    icon: React.ReactNode
    label: string
    variant: "default" | "secondary" | "destructive" | "outline"
  }
> = {
  running: {
    icon: <Loader2 className="text-primary size-4 animate-spin" />,
    label: "Running",
    variant: "secondary",
  },
  completed: {
    icon: <CheckCircle2 className="text-success size-4" />,
    label: "Completed",
    variant: "default",
  },
  failed: {
    icon: <AlertCircle className="text-destructive size-4" />,
    label: "Failed",
    variant: "destructive",
  },
  pending: {
    icon: <Clock className="text-muted-foreground size-4" />,
    label: "Pending",
    variant: "outline",
  },
  cancelled: {
    icon: <AlertCircle className="text-muted-foreground size-4" />,
    label: "Cancelled",
    variant: "outline",
  },
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return "—"
  if (seconds < 60) return `${Math.round(seconds)}s`
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60)
    const secs = Math.round(seconds % 60)
    return `${mins}m ${secs}s`
  }
  const hours = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  return `${hours}h ${mins}m`
}

function formatRelativeTime(timestamp: string | null | undefined): string {
  if (!timestamp) return "—"
  try {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)

    if (diffMins < 1) return "Just now"
    if (diffMins < 60) return `${diffMins}m ago`
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours}h ago`
    const diffDays = Math.floor(diffHours / 24)
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  } catch {
    return timestamp
  }
}

type JobHistoryPanelProps = {
  libraryId?: string
  onRetryFailed?: (libraryId: string) => void
}

export function JobHistoryPanel({ libraryId, onRetryFailed }: JobHistoryPanelProps) {
  const { t } = useTranslation()
  const [expandedJob, setExpandedJob] = useState<string | null>(null)

  const {
    data: jobsData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["admin", "indexing", "jobs", libraryId],
    queryFn: () => getIndexingJobs({ library_id: libraryId, page_size: 20 }),
    // Poll faster when jobs are running, slower otherwise
    refetchInterval: (query) => {
      const hasRunningJobs = query.state.data?.jobs.some((j) => j.status === "running")
      return hasRunningJobs ? 5000 : 30000 // 5s when running, 30s otherwise
    },
  })

  const { data: jobDetails, isLoading: isLoadingDetails } = useQuery({
    queryKey: ["admin", "indexing", "jobs", expandedJob, "details"],
    queryFn: () => getIndexingJobDetails(expandedJob!),
    enabled: !!expandedJob,
  })

  const handleToggleJob = (jobId: string) => {
    setExpandedJob(expandedJob === jobId ? null : jobId)
  }

  const getProgress = (job: IndexingJob) => {
    if (!job.files_total || job.files_total === 0) return 0
    const processed = job.files_processed + job.files_failed
    return Math.round((processed / job.files_total) * 100)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="flex items-center gap-2 font-medium">
          <History className="size-4" />
          {t("adminDashboard.indexing.jobHistory.title", "Recent Indexing Jobs")}
        </h4>
        <Button size="sm" type="button" variant="ghost" onClick={() => refetch()}>
          <RefreshCw className="size-4" />
        </Button>
      </div>

      <ScrollArea className="h-[400px]">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : isError ? (
          <div className="p-4 text-center">
            <AlertCircle className="text-muted-foreground mx-auto size-8" />
            <p className="text-muted-foreground mt-2 text-sm">
              {t("adminDashboard.indexing.jobHistory.loadError", "Failed to load job history")}
            </p>
            <Button className="mt-4" size="sm" variant="outline" onClick={() => refetch()}>
              {t("common.retry", "Retry")}
            </Button>
          </div>
        ) : !jobsData?.jobs.length ? (
          <div className="p-8 text-center">
            <Clock className="text-muted-foreground mx-auto size-8" />
            <p className="text-muted-foreground mt-2 text-sm">
              {t("adminDashboard.indexing.jobHistory.noJobs", "No indexing jobs found")}
            </p>
          </div>
        ) : (
          <div className="space-y-2 pr-4">
            {jobsData.jobs.map((job) => {
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              const status = (job.status as JobStatus) || "pending"
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending
              const isExpanded = expandedJob === job.job_id
              const progress = getProgress(job)

              return (
                <Collapsible key={job.job_id} open={isExpanded}>
                  <div className="bg-card overflow-hidden rounded-lg border">
                    <CollapsibleTrigger asChild>
                      <button
                        className="hover:bg-accent/50 flex w-full items-center gap-3 p-3 text-left"
                        type="button"
                        onClick={() => handleToggleJob(job.job_id)}
                      >
                        {isExpanded ? (
                          <ChevronDown className="size-4 shrink-0" />
                        ) : (
                          <ChevronRight className="size-4 shrink-0" />
                        )}
                        {config.icon}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate font-medium">{job.library_id}</span>
                            <Badge className="shrink-0" variant={config.variant}>
                              {config.label}
                            </Badge>
                          </div>
                          <div className="text-muted-foreground flex items-center gap-3 text-xs">
                            <span>{formatRelativeTime(job.started_at)}</span>
                            {job.files_total > 0 && (
                              <span>
                                {job.files_processed > 0 ? (
                                  <span className="text-success">
                                    {job.files_processed} indexed
                                  </span>
                                ) : job.files_failed > 0 ? (
                                  <span className="text-destructive">
                                    {job.files_failed} failed
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">
                                    {job.files_total} up-to-date
                                  </span>
                                )}
                              </span>
                            )}
                            {job.duration_seconds != null && (
                              <span>{formatDuration(job.duration_seconds)}</span>
                            )}
                          </div>
                        </div>
                        {status === "running" && job.files_total > 0 && (
                          <div className="w-24 shrink-0">
                            <Progress className="h-2" value={progress} />
                          </div>
                        )}
                      </button>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <div className="bg-muted/30 rounded-b-lg border-t p-3">
                        {isLoadingDetails && expandedJob === job.job_id ? (
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-full" />
                            <Skeleton className="h-4 w-3/4" />
                          </div>
                        ) : jobDetails && expandedJob === job.job_id ? (
                          <div className="space-y-3 text-sm">
                            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="cursor-help">
                                    <p className="text-muted-foreground text-xs">
                                      {t("adminDashboard.indexing.jobHistory.indexed", "Indexed")}
                                    </p>
                                    <p className="text-success font-medium">
                                      {jobDetails.files_processed}
                                    </p>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {t(
                                    "adminDashboard.indexing.jobHistory.indexedTooltip",
                                    "New or changed files that were indexed"
                                  )}
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="cursor-help">
                                    <p className="text-muted-foreground text-xs">
                                      {t("adminDashboard.indexing.jobHistory.failed", "Failed")}
                                    </p>
                                    <p className="text-destructive font-medium">
                                      {jobDetails.files_failed}
                                    </p>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {t(
                                    "adminDashboard.indexing.jobHistory.failedTooltip",
                                    "Files that encountered errors during indexing"
                                  )}
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="cursor-help">
                                    <p className="text-muted-foreground text-xs">
                                      {t(
                                        "adminDashboard.indexing.jobHistory.upToDate",
                                        "Up-to-date"
                                      )}
                                    </p>
                                    <p className="text-primary font-medium">
                                      {jobDetails.files_skipped}
                                    </p>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {t(
                                    "adminDashboard.indexing.jobHistory.upToDateTooltip",
                                    "Files already indexed with no changes detected"
                                  )}
                                </TooltipContent>
                              </Tooltip>
                              <div>
                                <p className="text-muted-foreground text-xs">
                                  {t("adminDashboard.indexing.jobHistory.total", "Total")}
                                </p>
                                <p className="font-medium">{jobDetails.files_total}</p>
                              </div>
                            </div>

                            {jobDetails.error_message && (
                              <div className="border-destructive/50 bg-destructive/10 rounded border p-2">
                                <p className="text-destructive text-xs font-medium">Error</p>
                                <p className="text-destructive text-xs">
                                  {jobDetails.error_message}
                                </p>
                              </div>
                            )}

                            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                            {jobDetails.failed_files && jobDetails.failed_files.length > 0 && (
                              <div className="space-y-1">
                                <p className="text-muted-foreground text-xs font-medium">
                                  {t(
                                    "adminDashboard.indexing.jobHistory.failedFiles",
                                    "Failed Files ({{count}})",
                                    { count: jobDetails.failed_files.length }
                                  )}
                                </p>
                                <ScrollArea className="h-24">
                                  <ul className="space-y-1 text-xs">
                                    {jobDetails.failed_files.slice(0, 20).map((file, idx) => (
                                      <li key={idx} className="text-muted-foreground truncate">
                                        • {typeof file === "string" ? file : file.filename}
                                      </li>
                                    ))}
                                    {jobDetails.failed_files.length > 20 && (
                                      <li className="text-muted-foreground">
                                        ...and {jobDetails.failed_files.length - 20} more
                                      </li>
                                    )}
                                  </ul>
                                </ScrollArea>
                              </div>
                            )}

                            {status === "failed" &&
                              jobDetails.files_failed > 0 &&
                              onRetryFailed && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => onRetryFailed(job.library_id)}
                                >
                                  <RefreshCw className="mr-2 size-3" />
                                  {t(
                                    "adminDashboard.indexing.jobHistory.retryFailed",
                                    "Retry Failed Files"
                                  )}
                                </Button>
                              )}
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-sm">
                            {t(
                              "adminDashboard.indexing.jobHistory.noDetails",
                              "No additional details available"
                            )}
                          </p>
                        )}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              )
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  )
}
